import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonValidatorService } from '../../../../../core/services/validators/common-validator.service';
import { ApiErrorService } from '../../../../../core/api/api-error.service';
import { SalesEngineService } from '../../../../../core/api/sales-engine.service';
import { SignalGet, SignalUpdate } from '../../../../../core/interfaces/sales-engine-interfaces';
import { ToastComponent } from '../../../../../shared/components/toast/toast.component';
import { firstValueFrom } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { LabeledInputComponent } from "../../../../../shared/components/labeled-input/labeled-input.component";
import { FormulaInputBoxComponent } from "../formula-input-box/formula-input-box.component";
import { TextareaInputComponent } from "../../../../../shared/components/textarea-input/textarea-input.component";
import { CustomButtonComponent } from "../../../../../shared/components/custom-button/custom-button.component";
import { TranslocoPipe } from '@jsverse/transloco';

@Component({
  selector: 'app-create-edit-signal',
  standalone: true,
  imports: [CommonModule, LabeledInputComponent, FormulaInputBoxComponent, TextareaInputComponent, CustomButtonComponent, TranslocoPipe],
  templateUrl: './create-edit-signal.component.html',
  styleUrl: './create-edit-signal.component.css'
})
export class CreateEditSignalComponent implements OnChanges {
  constructor(private commonValidator: CommonValidatorService, private apiError: ApiErrorService, private salesEngine: SalesEngineService) { }

  @Input() isOpen: boolean = false;
  @Output() isOpenChange = new EventEmitter<boolean>();

  @Input() isEditing: boolean = false;
  @Input() signal?: SignalGet;

  @Input() toast?: ToastComponent;
  @Input() reloadCallbackFunction?: () => void;

  // Inputs
  nameInput: string = '';
  nameInputError: string | null = null;

  descriptionInput: string = '';
  descriptionInputError: string | null = null;

  formulaInput: string = '';

  // Button click
  confirmClicked: boolean = false;

  ngOnChanges(changes: SimpleChanges): void {
    this.resetData();
    if (this.signal != undefined) {
      this.populateInputsFromSignal(this.signal)
    }
  }

  closeView() {
    this.isOpen = false;
    this.isOpenChange.emit(this.isOpen);
  }

  populateInputsFromSignal(signal: SignalGet) {
    this.nameInput = signal.name || '';
    this.descriptionInput = signal.description || '';

    // TODO: EVuk - implement logic for json
    this.formulaInput = signal.condition_formula;
  }

  resetData() {
    this.nameInput = this.descriptionInput = '';
    this.nameInputError = this.descriptionInputError = null;
    this.formulaInput = '';
  }

  validateInputs(): boolean {
    const nameInputValue = this.nameInput.trim();
    const descriptionInputValue = this.descriptionInput.trim();

    // Validate length for text inputs
    const nameError = this.commonValidator.validateLength(nameInputValue, 2, 255);
    const descriptionError = this.commonValidator.validateLength(descriptionInputValue, 2, 255, false);

    // Show errors
    this.nameInputError = nameError;
    this.descriptionInputError = descriptionError;

    // If there are any errors show them and prevent submission
    if (nameError || descriptionError) {
      return false;
    }

    return true;
  }

  getSignalFromInputs(id: string = ''): SignalUpdate {
    const result: SignalUpdate = {
      name: this.nameInput.trim(),
      description: this.descriptionInput,
      indicator_ids: [],
      condition_formula: this.formulaInput,
      logic: {}
    }
    return result;
  }

  async confirmButtonClick() {
    if (this.confirmClicked) {
      return;
    }
    this.confirmClicked = true;

    if (!this.validateInputs()) {
      this.confirmClicked = false;
      return;
    }

    // Send request for creating location
    try {
      if (this.signal) {
        const newData = this.getSignalFromInputs(this.signal.id);
        await firstValueFrom(this.salesEngine.updateSignal(this.signal.id, newData));
      }
      else {
        const newData = this.getSignalFromInputs();
        await firstValueFrom(this.salesEngine.createSignal(newData));
      }
    } catch (error) {
      var crid = 'UNKNOWN-CRID';
      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
      }
      this.toast?.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
      this.confirmClicked = false;
      return;
    }

    this.confirmClicked = false;
    this.isOpen = false;

    if (this.reloadCallbackFunction) {
      this.reloadCallbackFunction();
    }
  }
}
